import { MutableRefObject, PropsWithChildren, createContext, useContext, useEffect, useRef, useState } from "react";
import { useStateRef } from "../hooks/UseStateRef";
import { BandwidthState } from "../ui/video/VideoController";
import { useLogger } from "./LogProvider";
import { EnvironmentContext } from "./EnvironmentProvider";
import { Config } from "../environment/config";
import { SessionDataContext } from "./SessionDataProvider";
import { AuthContext } from "./AuthProvider";

export interface MetricsContext {
	logMetric: (props: LogMetricProps) => void;
	logAudioVideoMetric: (metric: BandwidthState) => void;
	metricState: MutableRefObject<Record<string, MetricState>>;
	/**
	 * Toggle showing the metrics debug on screen component
	 * @returns
	 */
	toggleDebugMetrics: () => void;
	AVMetrics: MutableRefObject<BandwidthState>;
}

export const MetricsContext = createContext<MetricsContext>({} as MetricsContext);
MetricsContext.displayName = "MetricsContext";

export interface MetricState {
	receivedAt: number;
	lastSentAt: number;
	lastSentValue?: number;
	value: number;
}

export interface LogMetricProps {
	key: string;
	value: number;
	numberOptions?: {
		round: boolean;
		divideBy?: number;
		/**
		 * Dont send metric if the difference between the last sent value
		 * and the incoming value is less than this
		 */
		reportingDeltaThreshold?: number;
	};
	/**
	 * override the default throttleSendInSeconds
	 */
	throttleSendInSeconds?: number;
}

interface MetricsProviderProps extends PropsWithChildren {
	metricDebugPopupLocation?: "bottom" | "above-site-footer";
}

export const MetricsProvider: React.FunctionComponent<MetricsProviderProps> = (props) => {
	const AuthCtx = useContext(AuthContext);
	const EnvCtx = useContext(EnvironmentContext);
	const logger = useLogger("providers.MetricsProvider");
	const sessionCtx = useContext(SessionDataContext);
	const metricState = useRef<Record<string, MetricState>>({});
	const AVMetrics = useRef<BandwidthState>();
	const [showMetricsDebug, setShowMetricsDebug, showMetricsDebugRef] = useStateRef(false);
	const [defaultThrottleSendInSeconds, setDefaultThrottleSendInSeconds] = useState(
		EnvCtx.get(Config.SEND_METRICS_THROTTLE_SECONDS, { optional: true }) || 180
	);

	const handleKeyDown = (event) => {
		if (event.metaKey && event.ctrlKey && event.key === "m") {
			toggleDebugMetrics();
		}
	};

	useEffect(() => {
		// Add event listener on component mount
		document.addEventListener("keydown", handleKeyDown);

		// Clean up the event listener on component unmount
		return () => {
			document.removeEventListener("keydown", handleKeyDown);
		};
	}, []); // Empty dependency array ensures that the effect runs only once on mount

	const toggleDebugMetrics = () => {
		setShowMetricsDebug(!showMetricsDebugRef.current);
	};

	const logMetric = ({ key, value, numberOptions, throttleSendInSeconds }: LogMetricProps) => {
		if (isNaN(value) || typeof value !== "number") return;
		metricState.current[key] = metricState.current[key] || { lastSentAt: 0, value: 0, receivedAt: 0 };

		let newValue = value;

		if (numberOptions?.divideBy) {
			newValue = newValue / numberOptions.divideBy;
		}

		if (numberOptions?.round) {
			newValue = Math.round(newValue);
		}

		/**
		 * Don't resent metric if it is the same value
		 */
		if (metricState.current[key].value === newValue) {
			return;
		}

		const now = new Date().getTime();
		metricState.current[key].receivedAt = now;
		metricState.current[key].value = newValue;

		/**
		 * Dont send if the difference between the last sent value
		 * and the incoming value is less than the reportingDeltaThreshold
		 */
		if (typeof metricState.current[key].lastSentValue !== "undefined") {
			const diff = Math.abs(metricState.current[key].lastSentValue - newValue);
			if (numberOptions?.reportingDeltaThreshold && diff < numberOptions.reportingDeltaThreshold) {
				return;
			}
		}

		/**
		 * Throttle sending the metric
		 */
		if (metricState.current[key].lastSentAt) {
			const diff = (now - metricState.current[key].lastSentAt) / 1000;
			const throttle = throttleSendInSeconds || defaultThrottleSendInSeconds;
			if (diff < throttle) {
				return;
			}
		}
		metricState.current[key].lastSentAt = now;
		metricState.current[key].lastSentValue = newValue;
		sendMetric(key, newValue);
	};

	const sendMetric = (key: string, value: number) => {
		if (EnvCtx.get(Config.FLAG_DISABLE_SEND_METRICS, { optional: true })) {
			return;
		}

		sessionCtx.saveMetric({
			name: `av/${key}`,
			source: AuthCtx.role === "patient" ? "user" : "agent",
			value: value.toString()
		});
	};

	const logAudioVideoMetric = (metric: BandwidthState) => {
		try {
			if (!metric?.fullMetrics) return;
			logMetric({
				key: "av.bandwidth.available_outbound_kbps",
				value: metric.metrics.availableOutboundBandwidthKbps,
				numberOptions: {
					round: true,
					reportingDeltaThreshold: 100
				}
			});

			logMetric({
				key: "av.bandwidth.available_inbound_kbps",
				value: metric.metrics.availableInboundBandwidthKbps,
				numberOptions: {
					round: true,
					reportingDeltaThreshold: 100
				}
			});

			logMetric({
				key: "av.bandwidth.round_trip_ms",
				value: metric.fullMetrics.currentRoundTripTimeMs,
				numberOptions: {
					round: true,
					reportingDeltaThreshold: 10
				}
			});

			logMetric({
				key: "av.bandwidth.audio_upstream_round_trip_ms",
				value: metric.fullMetrics.audioUpstreamRoundTripTimeMs,
				numberOptions: {
					round: true,
					reportingDeltaThreshold: 10
				}
			});

			logMetric({
				key: "av.bandwidth.video_upstream_kbps",
				value: metric.fullMetrics.videoUpstreamBitrate,
				numberOptions: {
					round: true,
					divideBy: 1000,
					reportingDeltaThreshold: 100
				}
			});

			logMetric({
				key: "av.bandwidth.audio_upstream_bitrate_jitter_ms",
				value: metric.fullMetrics.audioUpstreamJitterMs,
				numberOptions: {
					round: true,
					reportingDeltaThreshold: 10
				}
			});

			logMetric({
				key: "av.bandwidth.audio_downstream_bitrate_jitter_ms",
				value: metric.fullMetrics.audioDownstreamJitterMs,
				numberOptions: {
					round: true,
					reportingDeltaThreshold: 10
				}
			});

			AVMetrics.current = metric;
		} catch (error) {
			logger.warn("logAudioVideoMetric", "Error", error);
		}
	};

	const value: MetricsContext = {
		logMetric,
		logAudioVideoMetric,
		metricState,
		AVMetrics,
		toggleDebugMetrics
	};

	return (
		<MetricsContext.Provider value={value}>
			<>
				{showMetricsDebug && <MetricsDebugComponent {...props} />}
				{props.children}
			</>
		</MetricsContext.Provider>
	);
};

/**
 * A debug component that shows the current metrics state on screen
 * @param props
 * @returns
 */
export const MetricsDebugComponent = (props: MetricsProviderProps) => {
	const MetricsCtx = useContext(MetricsContext);

	const [localMetrics, setLocalMetrics] = useState<Record<string, MetricState>>(MetricsCtx.metricState.current);
	const [forcedReRender, setForcedReRender] = useState(0);

	useEffect(() => {
		const timer = setTimeout(() => {
			setLocalMetrics(MetricsCtx.metricState.current);
			setForcedReRender(forcedReRender + 1);
			//purposefully not using logger
			console.log("av_metric", MetricsCtx.AVMetrics?.current?.fullMetrics);
		}, 1000);
		return () => {
			clearInterval(timer);
		};
	}, [forcedReRender]);

	return (
		<div
			style={{
				position: "fixed",
				width: "40%",
				wordBreak: "break-word",
				right: 0,
				bottom: props?.metricDebugPopupLocation === "above-site-footer" ? 72 : 0,
				zIndex: 1000,
				backgroundColor: "rgba(0,0,0,.8)",
				color: "white",
				fontSize: 14,
				padding: 10
			}}
		>
			{Object.keys(localMetrics).map((key) => {
				return (
					<div key={key}>
						<div>
							{key}: {localMetrics[key].value}
						</div>
					</div>
				);
			})}
			<span style={{ color: "grey" }}>full metrics in console</span>
		</div>
	);
};
